import { PrimaryButton } from '@fluentui/react';
import { useContext, useEffect, useState } from 'react';
import { getProviderCategoryByPosition } from './Util';
import { Pod } from '../../api/pods.types';
import { MAX_PATIENTS_BY_CATEGORY, SUPPORT_STAFF_POSITIONS } from '../../config/constants';
import PodCapacity from './PodCapacity';
import PodDetailItem, { PodDetailItemShimmer } from './PodDetailItem';
import styles from './PodDetailsPanel.module.scss';
import PodMarketChips from './PodMarketChips';
import PodStatusSpinner, { PodStatusSpinnerState } from './PodStatusSpinner';
import { PodStateContext } from './state/reducer';
import { getUserState } from './state/selectors';

interface Props {
  pod?: Pod,
  savingState?: PodStatusSpinnerState,
  onSave: (name: string, description: string) => void;
}

const PodDetailsPanel = ({pod, savingState, onSave}: Props) => {
  const [editorNameValue, setEditorNameValue] = useState(pod?.name ?? '');
  const [editorDescriptionValue, setEditorDescriptionValue] = useState(pod?.description ?? '');

  const {useContextSelector} = useContext(PodStateContext);
  const userIsOpsManager = useContextSelector(getUserState).isManager;

  // If the incoming pod data changes, reset the editor fields.
  useEffect(() => {
    setEditorNameValue(pod?.name ?? '');
    setEditorDescriptionValue(pod?.description ?? '');
  }, [pod]);

  const nameUnsaved = editorNameValue !== pod?.name;
  const descriptionUnsaved = editorDescriptionValue !== pod?.description;

  // Enable the save button if one of the editable fields has changed, plus none of the fields are blank.
  const anyUnsaved = nameUnsaved || descriptionUnsaved;
  const unfilledCount = +!editorNameValue + +!editorDescriptionValue;
  const enableSave = anyUnsaved && unfilledCount === 0 && savingState !== PodStatusSpinnerState.loading;

  if (!pod) {
    return null;
  }

  const handleSave = () => {
    onSave(editorNameValue, editorDescriptionValue);
  }

  const editDisabled = savingState === PodStatusSpinnerState.loading || !userIsOpsManager;

  const disabledTooltipText = savingState === PodStatusSpinnerState.loading
    ? "Can’t edit while saving"
    : "Editing is disabled in read-only mode."

  return (
    <div className={styles.detailsSidePanel}>
      <div className={styles.header}>
        <div className={styles.headerText}>Details</div>
      </div>
      <div className={styles.content}>
      <PodDetailItem
          type="text"
          title="Name"
          value={editorNameValue}
          disabled={editDisabled}
          disabledTooltip={disabledTooltipText}
          emptyValueClassName={styles.emptyItemValue}
          onCommit={setEditorNameValue}
          onReset={() => {
            setEditorNameValue(pod.name);
            return pod.name;
          }}
          hasUnsavedChanges={nameUnsaved}
        />
        <PodDetailItem
          type="textarea"
          title="Description"
          value={editorDescriptionValue}
          disabled={editDisabled}
          disabledTooltip={disabledTooltipText}
          emptyValueClassName={styles.emptyItemValue}
          onCommit={setEditorDescriptionValue}
          onReset={() => {
            setEditorDescriptionValue(pod.description)
            return pod.description;
          }}
          hasUnsavedChanges={descriptionUnsaved}
        />
        <PodDetailItem
          type="readonly"
          title="Region"
          value={pod.region}
        />
        <PodDetailItem
          type="readonly"
          title="Market groups"
          value={pod.markets}
          onRender={(currentValue) => {
            if (currentValue.length) return <PodMarketChips chips={currentValue} noTruncate/>;
            else return <em>No market groups selected</em>;
          }}
          emptyValueClassName={styles.emptyItemValue}
        />
        <PodDetailItem
          type="readonly"
          title="Patients"
          value=""
          onRender={() => {
            const podPhysicianClinicians = pod.providers.filter(provider => !(SUPPORT_STAFF_POSITIONS.includes(provider.position)));

            const count = podPhysicianClinicians.reduce((c, provider) => {
              const category = getProviderCategoryByPosition(provider);

              if (category === undefined || provider.status !== 'ACTIVATED') return c;
              else return c + provider.patientCount;
            }, 0);
            const max = podPhysicianClinicians.reduce((c, provider) => {
              const category = getProviderCategoryByPosition(provider);

              if (category === undefined || provider.status !== 'ACTIVATED') return c;
              else return c + MAX_PATIENTS_BY_CATEGORY[category];
            }, 0);

            return <PodCapacity count={count} max={max}/>;
          }}
        />
        <div className={styles.saveRow}>
          <div className={styles.saveError} aria-live="polite">
            {unfilledCount === 1 && !editorNameValue.length && 'Name must be provided.'}
            {unfilledCount === 1 && !editorDescriptionValue.length && 'Description must be provided.'}
            {unfilledCount > 1 && 'All fields must be provided.'}
          </div>
          {savingState !== undefined && <PodStatusSpinner state={savingState}/>}
          <PrimaryButton
            className={styles.saveButton}
            disabled={!enableSave}
            onClick={handleSave}
            text={savingState === PodStatusSpinnerState.loading ? 'Saving...' : 'Save'}
          />
        </div>
      </div>
    </div>
  )
};

export const PodDetailsPanelShimmer = () => {
  return (
    <div className={`${styles.detailsSidePanel} ${styles.sidePanelShimmer}`}>
      <div className={styles.header}>
        <div className={styles.headerText}>Details</div>
      </div>
      <div className={styles.content}>
        <PodDetailItemShimmer title="Name" />
        <PodDetailItemShimmer title="Description" />
        <PodDetailItemShimmer title="Region" noEdit />
        <PodDetailItemShimmer title="Market groups" noEdit />
        <PodDetailItemShimmer title="Patients" noEdit />
        <div className={styles.saveRow}>
          <div className={styles.saveError}></div>
          <PrimaryButton
            className={styles.saveButton}
            disabled
            text="Save"
          />
        </div>
      </div>
    </div>
  );
}

export default PodDetailsPanel;